import React, { useState, useEffect } from 'react';
import api from '../../utils/axiosConfig';
import { useNavigate } from 'react-router-dom';
import { formatDateDDMMYYYY } from '../../utils/dateFormat';
import { getDefaultFinancialsDateRange } from '../../utils/financialsDateRange';
import { exportFinancialsListPdf } from '../../utils/financialsListPdf';
import FinancialsDateRangeFilter from '../../components/FinancialsDateRangeFilter/FinancialsDateRangeFilter';
import PageLoadingArea from '../../components/PageLoader/PageLoadingArea';
import usePageLoading from '../../hooks/usePageLoading';
import '../../styles/ListPage.css';

function formatMoney(value) {
    const num = parseFloat(value);
    if (Number.isNaN(num)) return '—';
    return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function ExpenseList() {
    const navigate = useNavigate();
    const { isLoading, withLoading } = usePageLoading(true);
    const defaultRange = getDefaultFinancialsDateRange();
    const [fromDate, setFromDate] = useState(defaultRange.fromDate);
    const [toDate, setToDate] = useState(defaultRange.toDate);
    const [expenses, setExpenses] = useState([]);
    const [selectedExpenses, setSelectedExpenses] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredExpenses, setFilteredExpenses] = useState([]);

    useEffect(() => {
        withLoading(async () => {
            const res = await api.get('/expenses', {
                params: { fromDate, toDate }
            });
            setExpenses(res.data);
            setFilteredExpenses(res.data);
            setSelectedExpenses([]);
        }).catch((err) => {
            console.error('Error fetching expenses:', err);
            const message = err.response?.data?.Error || err.message || 'Error fetching expenses';
            alert(message);
        });
    }, [fromDate, toDate, withLoading]);

    useEffect(() => {
        const term = searchTerm.toLowerCase();
        const results = expenses.filter((entry) =>
            (entry.description || '').toLowerCase().includes(term) ||
            (entry.paymentType || '').toLowerCase().includes(term) ||
            (entry.vendorName || '').toLowerCase().includes(term)
        );
        setFilteredExpenses(results);
    }, [searchTerm, expenses]);

    const handleCheckboxChange = (expenseId) => {
        setSelectedExpenses((prev) =>
            prev.includes(expenseId) ? prev.filter((id) => id !== expenseId) : [...prev, expenseId]
        );
    };

    const handleSelectAll = (e) => {
        setSelectedExpenses(e.target.checked ? filteredExpenses.map((entry) => entry.id) : []);
    };

    const handleDeleteSelected = () => {
        if (!window.confirm('Are you sure you want to delete the selected expenses?')) return;
        Promise.all(selectedExpenses.map((expenseId) => api.delete(`/expenses/${expenseId}`)))
            .then(() => {
                setExpenses((prev) => prev.filter((entry) => !selectedExpenses.includes(entry.id)));
                setSelectedExpenses([]);
            })
            .catch(() => {
                alert('Error deleting expenses');
            });
    };

    const allSelected = filteredExpenses.length > 0 && selectedExpenses.length === filteredExpenses.length;

    const handleRangeChange = (nextFromDate, nextToDate) => {
        setFromDate(nextFromDate);
        setToDate(nextToDate);
    };

    const exportExpensePdf = async () => {
        if (filteredExpenses.length === 0) {
            alert('No expense entries to export');
            return;
        }

        const totalAmount = filteredExpenses.reduce(
            (sum, entry) => sum + parseFloat(entry.amount || 0),
            0
        );

        try {
            await exportFinancialsListPdf({
                pageTitle: 'Expense',
                fileName: `expense_${fromDate}_to_${toDate}.pdf`,
                fromDate,
                toDate,
                searchTerm,
                head: [['Date', 'Amount', 'Payment type', 'Description', 'Vendor']],
                body: filteredExpenses.map((entry) => [
                    formatDateDDMMYYYY(entry.date),
                    formatMoney(entry.amount),
                    entry.paymentType || '',
                    entry.description || '',
                    entry.vendorName || '—'
                ]),
                totalAmount: formatMoney(totalAmount),
                columnStyles: {
                    0: { cellWidth: 22 },
                    1: { halign: 'right' },
                    2: { cellWidth: 24 }
                }
            });
        } catch (error) {
            console.error('Error exporting expense PDF:', error);
            alert('Error exporting expense PDF');
        }
    };

    return (
        <PageLoadingArea isLoading={isLoading}>
            <div className="list-page">
                <div className="list-page-header">
                    <div className="list-page-title">
                        <h1>Expense</h1>
                        <p className="list-page-subtitle">
                            {filteredExpenses.length} entr{filteredExpenses.length === 1 ? 'y' : 'ies'} · {formatDateDDMMYYYY(fromDate)} to {formatDateDDMMYYYY(toDate)}
                        </p>
                    </div>
                    <div className="list-header-actions">
                        <div className="list-search-wrap">
                            <input
                                type="text"
                                placeholder="Search description, payment type, or vendor"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="list-search-input"
                            />
                        </div>
                        <div className="list-button-group">
                            <button
                                type="button"
                                onClick={() => navigate('/financials')}
                                className="list-secondary-button"
                            >
                                Back to financials
                            </button>
                            <button
                                type="button"
                                onClick={exportExpensePdf}
                                className="list-secondary-button"
                                disabled={filteredExpenses.length === 0}
                            >
                                Export PDF
                            </button>
                            <button type="button" onClick={() => navigate('/createExpense')} className="list-add-button">
                                Add expense
                            </button>
                            {selectedExpenses.length > 0 && (
                                <button type="button" onClick={handleDeleteSelected} className="list-delete-button">
                                    Delete ({selectedExpenses.length})
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <FinancialsDateRangeFilter
                    fromDate={fromDate}
                    toDate={toDate}
                    onRangeChange={handleRangeChange}
                />

                <div className="list-table-card">
                    <div className="list-table-scroll">
                        <table className="list-table">
                            <thead>
                                <tr>
                                    <th className="col-check">
                                        <input type="checkbox" checked={allSelected} onChange={handleSelectAll} aria-label="Select all" />
                                    </th>
                                    <th>Date</th>
                                    <th>Amount</th>
                                    <th>Payment type</th>
                                    <th>Description</th>
                                    <th>Vendor</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredExpenses.length === 0 ? (
                                    <tr className="list-empty-row">
                                        <td colSpan="6">
                                            <div className="list-empty-state">
                                                <h3>No expense entries found</h3>
                                                <p>{searchTerm ? 'Try a different search term.' : 'Add your first expense entry to get started.'}</p>
                                                {!searchTerm && (
                                                    <button
                                                        type="button"
                                                        className="list-add-button"
                                                        onClick={() => navigate('/createExpense')}
                                                    >
                                                        Add expense
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ) : filteredExpenses.map((entry) => (
                                    <tr
                                        key={entry.id}
                                        className={`list-row${selectedExpenses.includes(entry.id) ? ' is-selected' : ''}`}
                                        onClick={(e) => {
                                            if (e.target.type !== 'checkbox') navigate(`/createExpense/${entry.id}`);
                                        }}
                                    >
                                        <td className="col-check" onClick={(e) => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                checked={selectedExpenses.includes(entry.id)}
                                                onChange={() => handleCheckboxChange(entry.id)}
                                            />
                                        </td>
                                        <td>{formatDateDDMMYYYY(entry.date)}</td>
                                        <td className="col-num">{formatMoney(entry.amount)}</td>
                                        <td>{entry.paymentType}</td>
                                        <td><span className="list-primary-text">{entry.description}</span></td>
                                        <td>{entry.vendorName || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </PageLoadingArea>
    );
}

export default ExpenseList;
